/**
 * 48px. `[logo]   Markets  Portfolio  Leaderboard  │  $1,240.55  │  0x7a…3f ▾`
 *
 * Below 768px: `[logo]  $1,240.55  🔔  ◉  ☰`. The account chip is an icon
 * there (`<ConnectButton>`), which is what makes room for the name, and the
 * four destinations go behind `<PhoneNav>` — the row was ~650px of content that
 * could not shrink, so on a phone it widened the page instead of fitting it
 * (`e2e/responsive.spec.ts`).
 *
 * The balance and account slots are passed in rather than read here, because
 * `<BalanceChip>` renders five components separately and never conflates them
 * (invariant 9) and `<ConnectButton>` pulls in the wallet chunk, which Phase 4's
 * cold load must not do for an anonymous visitor. The header is a shell with
 * holes in it until Phase 5 and Phase 8 fill them.
 */

import type { ReactNode } from 'react'
import { NavLink } from 'react-router-dom'
import { cx } from '@/lib/cx'
import { UptikLogo } from '@/components/primitives'
import { PhoneNav } from './PhoneNav'

export interface NavItem {
  to: string
  label: string
}

export const NAV: readonly NavItem[] = [
  { to: '/trade/btc', label: 'Markets' },
  { to: '/portfolio', label: 'Portfolio' },
  { to: '/history', label: 'History' },
  { to: '/leaderboard', label: 'Leaderboard' },
  { to: '/stats', label: 'Stats' },
]

export interface HeaderProps {
  balance?: ReactNode
  account?: ReactNode
  /** The bell. Passed in, like the other two, because it subscribes a store. */
  notifications?: ReactNode
  className?: string
}

export function Header({ balance, account, notifications, className }: HeaderProps) {
  return (
    <header
      className={cx(
        // `relative` is for `<PhoneNav>`'s panel, which spans the header's width.
        'relative flex h-[var(--ud-header-height)] shrink-0 items-center gap-2 bg-bg-surface px-2',
        'md:gap-4 md:px-3',
        className,
      )}
    >
      <NavLink
        to="/trade/btc"
        className={cx(
          'flex min-h-[var(--ud-tap-min)] min-w-[var(--ud-tap-min)] shrink-0 items-center justify-center',
          'text-text-primary focus-visible:shadow-focus focus-visible:outline-none',
          'md:min-h-0 md:min-w-0',
        )}
      >
        <UptikLogo />
      </NavLink>

      <nav aria-label="primary" className="hidden items-center gap-3 md:flex">
        {NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              cx(
                // `min-h` below 768px only: components.md §1's hit-target floor.
                // The header is 48px, so filling it costs no layout and the
                // `md:` revert keeps the desktop nav its designed height.
                'flex min-h-[var(--ud-tap-min)] items-center md:min-h-0',
                'text-body transition-colors duration-[var(--ud-duration-instant)]',
                'focus-visible:shadow-focus focus-visible:outline-none',
                isActive ? 'text-text-primary' : 'text-text-secondary hover:text-text-primary',
              )
            }
          >
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="ml-auto flex min-w-0 items-center gap-2 md:gap-3">
        {balance ? (
          <>
            {balance}
            <span aria-hidden="true" className="hidden h-4 w-px bg-border-subtle md:block" />
          </>
        ) : null}
        {notifications}
        {account}
      </div>

      <PhoneNav items={NAV} />
    </header>
  )
}
